import React from "react";
import { AppProvider, Frame, Navigation, TopBar } from "@shopify/polaris";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  AppsIcon,
  TargetIcon,
  GiftCardIcon,
  PersonSegmentIcon,
  ChartVerticalFilledIcon,
  SettingsIcon,
} from "@shopify/polaris-icons";
import DashboardPage from "./pages/DashboardPage";
import GamesCatalogPage from "./pages/GamesCatalogPage";
import CampaignsListPage from "./pages/CampaignsListPage";
import CampaignFormPage from "./pages/CampaignFormPage";
import RewardsPage from "./pages/RewardsPage";
import GamePlaygroundPage from "./pages/GamePlaygroundPage";
import GamePreviewPage from "./pages/GamePreviewPage";
import SegmentsPage from "./pages/SegmentsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import CampaignAnalyticsPage from "./pages/CampaignAnalyticsPage";
import SettingsPage from "./pages/SettingsPage";
import FraudDetectionPage from "./pages/FraudDetectionPage";
import { initializeAppBridge, isEmbedded } from "./utils/app-bridge";

function App() {
  const navigate = useNavigate();
  const [mobileNavigationActive, setMobileNavigationActive] = React.useState(false);
  const embedded = isEmbedded();

  // Initialize App Bridge on mount (if embedded in Shopify)
  React.useEffect(() => {
    if (embedded) {
      try {
        // Initialize App Bridge (this also sets up window.shopifyApp)
        initializeAppBridge();
      } catch (error) {
        console.error("[App Bridge] Initialization failed:", error);
      }
    } else {
      console.warn("[App Bridge] Not running in embedded context (development mode)");
    }
  }, [embedded]);

  const toggleMobileNavigationActive = () => {
    setMobileNavigationActive((prev) => !prev);
  };

  // Only show TopBar when NOT embedded (development mode)
  const topBarMarkup = !embedded ? (
    <TopBar showNavigationToggle onNavigationToggle={toggleMobileNavigationActive} />
  ) : undefined;

  const navigationMarkup = (
    <Navigation location="/">
      <Navigation.Section
        items={[
          {
            label: "Dashboard",
            icon: HomeIcon,
            onClick: () => navigate("/"),
          },
          {
            label: "Games",
            icon: AppsIcon,
            onClick: () => navigate("/games"),
          },
          {
            label: "Campaigns",
            icon: TargetIcon,
            onClick: () => navigate("/campaigns"),
          },
          {
            label: "Rewards",
            icon: GiftCardIcon,
            onClick: () => navigate("/rewards"),
          },
          {
            label: "Segments",
            icon: PersonSegmentIcon,
            onClick: () => navigate("/segments"),
          },
          {
            label: "Analytics",
            icon: ChartVerticalFilledIcon,
            onClick: () => navigate("/analytics"),
          },
          {
            label: "Settings",
            icon: SettingsIcon,
            onClick: () => navigate("/settings"),
          },
        ]}
      />
    </Navigation>
  );

  return (
    <AppProvider i18n={{}}>
      <Frame
        topBar={topBarMarkup}
        navigation={navigationMarkup}
        showMobileNavigation={mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigationActive}
      >
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/games" element={<GamesCatalogPage />} />
          <Route path="/campaigns" element={<CampaignsListPage />} />
          <Route path="/campaigns/:id" element={<CampaignFormPage />} />
          <Route path="/campaigns/:id/analytics" element={<CampaignAnalyticsPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/segments" element={<SegmentsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/fraud" element={<FraudDetectionPage />} />
          <Route path="/playground" element={<GamePlaygroundPage />} />
          <Route path="/games/preview" element={<GamePreviewPage />} />
        </Routes>
      </Frame>
    </AppProvider>
  );
}

export default App;
